package org.teddapp.project.ui

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.horizontalDrag
import androidx.compose.ui.input.pointer.positionChange
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.layout.layout
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.roundToInt

/** De quel côté de l'écran vit le panneau : la poignée est sur son bord intérieur. */
enum class PaneSide { Left, Right }

/**
 * Les bornes d'un panneau latéral. Entre [min] et [max], il suit la poignée. Tiré sous
 * [collapseBelow], il prend sa forme réduite, large de [collapsed], et n'en bouge plus :
 * il se rouvre quand on le tire de nouveau au-delà de [collapseBelow].
 */
class PaneSpec(
    /** Le nom sous lequel l'appareil retient la largeur choisie. */
    val key: String,
    val default: Dp,
    val min: Dp,
    val max: Dp,
    val collapsed: Dp = 56.dp,
    val collapseBelow: Dp = maxOf(collapsed + 24.dp, min - 64.dp),
)

/** Ce que la poignée décide d'une largeur tirée : la forme réduite, ou une largeur dans les bornes. */
object PaneMath {
    data class Shape(val collapsed: Boolean, val width: Dp)

    fun shape(spec: PaneSpec, raw: Dp): Shape =
        if (raw < spec.collapseBelow) Shape(true, spec.collapsed)
        else Shape(false, raw.coerceIn(spec.min, spec.max))

    /** La forme retenue : « reduit », un nombre de dp, ou rien (la largeur par défaut). */
    fun restore(spec: PaneSpec, stored: String?): Shape = when {
        stored == COLLAPSED -> Shape(true, spec.collapsed)
        else -> Shape(false, (stored?.toFloatOrNull()?.dp ?: spec.default).coerceIn(spec.min, spec.max))
    }

    fun store(shape: Shape): String = if (shape.collapsed) COLLAPSED else shape.width.value.toInt().toString()

    const val COLLAPSED = "reduit"
}

/**
 * Le padding du contenu d'un panneau, selon le côté où vit sa poignée : la poignée
 * apporte déjà [HANDLE_AIR] d'air, le contenu n'en ajoute que [TeddSpace.S2] de ce
 * côté-là. Les écrans n'ajoutent rien entre un panneau et son voisin : c'est le
 * panneau qui porte ses marges, et elles suivent la largeur réglée.
 */
fun paneContentPadding(side: PaneSide): PaddingValues = when (side) {
    PaneSide.Left -> PaddingValues(start = TeddSpace.S4, top = TeddSpace.S3, end = TeddSpace.S2, bottom = TeddSpace.S4)
    PaneSide.Right -> PaddingValues(start = TeddSpace.S2, top = TeddSpace.S3, end = TeddSpace.S4, bottom = TeddSpace.S4)
}

/** Largeur de la poignée : un filet de 1 dp au milieu, de l'air de chaque côté. */
val HANDLE_WIDTH: Dp = 9.dp

/** L'air entre le filet de la poignée et ce qui la borde. */
val HANDLE_AIR: Dp = 4.dp

/**
 * Un panneau latéral dont on règle la largeur à la poignée. Trop réduit, il prend sa
 * forme réduite : [collapsedContent], ou par défaut une bande avec [icon] pour le
 * rouvrir. Un double-clic sur la poignée rend la largeur par défaut. La largeur choisie
 * est retenue sur l'appareil.
 *
 * Le contenu reçoit [contentPadding] ([paneContentPadding] par défaut) : un écran n'a
 * pas à l'entourer de marges, ni à en mettre entre le panneau et ce qui le suit.
 */
@Composable
fun TeddSidePane(
    spec: PaneSpec,
    modifier: Modifier = Modifier,
    side: PaneSide = PaneSide.Left,
    title: String = "",
    icon: String = "list",
    contentPadding: PaddingValues = paneContentPadding(side),
    collapsedContent: (@Composable (expand: () -> Unit) -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val settings = LocalAppSettings.current
    val stored = settings.pane(spec.key)?.takeIf { it.isNotEmpty() }
    var shape by remember(spec.key) { mutableStateOf(PaneMath.restore(spec, stored)) }
    var dragging by remember { mutableStateOf(false) }
    // Vrai dès que la poignée a bougé : un clic sans déplacement n'enregistre rien.
    var moved by remember { mutableStateOf(false) }
    // Une valeur retenue qui change ailleurs (autre fenêtre, remise à zéro) s'applique ;
    // celle qu'on vient d'enregistrer soi-même est déjà la forme courante.
    LaunchedEffect(stored) {
        val restored = PaneMath.restore(spec, stored)
        if (!dragging && restored != shape) shape = restored
    }
    var raw by remember { mutableStateOf(shape.width) }
    val width by animateDpAsState(
        targetValue = shape.width,
        animationSpec = if (dragging) snap() else tween(160),
        label = "largeur du panneau",
    )
    fun commit(next: PaneMath.Shape) {
        // Retenue au dp entier : la forme courante est celle qui sera relue.
        val stored = PaneMath.store(next)
        shape = PaneMath.restore(spec, stored)
        settings.applyPane(spec.key, stored)
    }
    val expand = { commit(PaneMath.Shape(false, spec.default.coerceIn(spec.min, spec.max))) }

    val panel: @Composable () -> Unit = {
        // La largeur se lit à la mise en page, pas à la composition : tirer la poignée
        // remesure le panneau sans recomposer ce qu'il contient.
        Box(Modifier.paneWidth { width }.fillMaxHeight().clipToBounds()) {
            if (shape.collapsed) {
                collapsedContent?.invoke(expand) ?: CollapsedStrip(title, icon, expand)
            } else {
                Box(Modifier.fillMaxSize().padding(contentPadding)) {
                    RecompositionProbe("pane.${spec.key}")
                    content()
                }
            }
        }
    }
    val density = LocalDensity.current
    val handle: @Composable () -> Unit = {
        PaneHandle(
            dragging = dragging,
            onStart = { dragging = true; moved = false; raw = shape.width },
            onDrag = { px ->
                moved = true
                val dx = with(density) { px.toDp() }
                raw += if (side == PaneSide.Left) dx else -dx
                // Au dp entier : les fractions de pixel ne valent pas une mise en page.
                val next = PaneMath.shape(spec, raw.value.roundToInt().dp)
                if (next != shape) shape = next
            },
            onEnd = { dragging = false; if (moved) commit(shape) },
            onReset = { settings.applyPane(spec.key, null); shape = PaneMath.restore(spec, null) },
        )
    }
    Row(modifier.fillMaxHeight()) {
        if (side == PaneSide.Left) { panel(); handle() } else { handle(); panel() }
    }
}

/** Une largeur lue au moment de mesurer : la changer ne recompose rien. */
private fun Modifier.paneWidth(width: () -> Dp): Modifier = layout { measurable, constraints ->
    val w = width().roundToPx().coerceAtLeast(0)
    val placeable = measurable.measure(constraints.copy(minWidth = w, maxWidth = w))
    layout(w, placeable.height) { placeable.place(0, 0) }
}

/** La poignée : une ligne fine, plus marquée au survol et pendant qu'on la tire, large de [HANDLE_WIDTH]. */
@Composable
private fun PaneHandle(dragging: Boolean, onStart: () -> Unit, onDrag: (Float) -> Unit, onEnd: () -> Unit, onReset: () -> Unit) {
    val interaction = remember { MutableInteractionSource() }
    val hovered by interaction.collectIsHoveredAsState()
    val active = hovered || dragging
    Box(
        modifier = Modifier
            .width(HANDLE_WIDTH)
            .fillMaxHeight()
            .hoverable(interaction)
            .pointerHoverIcon(ResizeHorizontalCursor)
            .semantics { contentDescription = "Régler la largeur du panneau" }
            .pointerInput(Unit) {
                // Suivi direct, sans seuil de départ : la colonne colle au pointeur dès le
                // premier pixel. Les écarts sont ceux de l'écran, même quand la poignée bouge.
                awaitEachGesture {
                    val down = awaitFirstDown(requireUnconsumed = false)
                    onStart()
                    horizontalDrag(down.id) { change ->
                        val dx = change.positionChange().x
                        change.consume()
                        onDrag(dx)
                    }
                    onEnd()
                }
            }
            .pointerInput(Unit) { detectTapGestures(onDoubleTap = { onReset() }) },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            Modifier
                .width(if (active) 3.dp else 1.dp)
                .fillMaxHeight()
                .background(if (active) TeddTokens.LimeStrong else TeddTokens.Line),
        )
    }
}

/** La forme réduite par défaut : une bande, son icône, et de quoi rouvrir le panneau. */
@Composable
private fun CollapsedStrip(title: String, icon: String, expand: () -> Unit) {
    Column(
        modifier = Modifier.fillMaxSize().padding(top = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        TeddIconButton(onClick = expand, modifier = Modifier.semantics { contentDescription = "Rouvrir ${title.ifBlank { "le panneau" }}" }) {
            TeddIcon(name = "chevron-right", size = 16.dp, color = TeddTokens.InkMuted)
        }
        TeddIconButton(onClick = expand) { TeddIcon(name = icon, size = 18.dp, color = TeddTokens.InkMuted) }
    }
}

/** Un élément de la forme réduite d'un panneau : une icône qu'on touche. */
class PaneRailItem(
    val key: String,
    val label: String,
    val icon: String,
    val selected: Boolean = false,
    val color: androidx.compose.ui.graphics.Color? = null,
    val onClick: () -> Unit,
)

/**
 * La forme réduite d'un panneau qui liste des éléments : de quoi le rouvrir, puis une
 * icône par élément, la sélection cerclée. Les éléments au-delà de la hauteur défilent.
 */
@Composable
fun TeddPaneRail(expand: () -> Unit, items: List<PaneRailItem>, title: String = "") {
    Column(
        modifier = Modifier.fillMaxSize().padding(top = 12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TeddIconButton(onClick = expand, modifier = Modifier.semantics { contentDescription = "Rouvrir ${title.ifBlank { "le panneau" }}" }) {
            TeddIcon(name = "chevron-right", size = 16.dp, color = TeddTokens.InkMuted)
        }
        androidx.compose.foundation.lazy.LazyColumn(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(6.dp),
            modifier = Modifier.fillMaxSize(),
        ) {
            items(items.size, key = { items[it].key }) { i ->
                val item = items[i]
                val shape = androidx.compose.foundation.shape.RoundedCornerShape(TeddTokens.RSm)
                Box(
                    modifier = Modifier
                        .padding(2.dp)
                        .then(if (item.selected) Modifier.background(TeddTokens.SurfaceSunken, shape) else Modifier)
                        .semantics { contentDescription = item.label },
                ) {
                    TeddIconButton(onClick = item.onClick, active = item.selected) {
                        TeddIcon(name = item.icon, size = 16.dp, color = item.color ?: if (item.selected) TeddTokens.Ink else TeddTokens.InkMuted)
                    }
                }
            }
        }
    }
}
